import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { doc, getDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, getBytes } from 'firebase/storage';
import { sopJsonToHtml } from '../components/sop/tiptap/sopJson';

/**
 * Render a Tiptap document (contentJSON) to a branded, paginated PDF — the same
 * output the SOP editor produces. Shared so SOPs and Job Descriptions match.
 * Loads the user's company logo/name/colors, renders the document HTML via
 * sopJsonToHtml, slices the html2canvas render across A4 pages with a logo header
 * and company/date/page-number footer.
 *
 * @param {object} opts
 * @param {string} opts.title - document title (shown as the H1)
 * @param {object} opts.contentJSON - Tiptap JSON
 * @param {object} opts.currentUser - Firebase auth user (for branding lookup)
 * @param {string} [opts.fileSuffix='document'] - appended to the download filename
 */
export async function generateDocumentPdf({ title, contentJSON, currentUser, fileSuffix = 'document' }) {
  let companyLogo = null;
  let companyName = '';

  if (currentUser) {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const d = userDoc.data();
        if (d.companyLogo) companyLogo = d.companyLogo;
        if (d.company) companyName = d.company;
      }
      if (!companyLogo) {
        const internalDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (internalDoc.exists()) {
          const d = internalDoc.data();
          if (d.companyLogo) companyLogo = d.companyLogo;
          if (!companyName && d.company) companyName = d.company;
        }
      }
    } catch (err) {
      console.error('Error fetching branding for PDF:', err);
    }
  }

  const loadLogoAsDataUrl = async (url) => {
    try {
      const isFirebaseStorageURL =
        url.includes('firebasestorage.googleapis.com') || url.includes('firebasestorage.app');
      if (isFirebaseStorageURL && currentUser) {
        const urlMatch = url.match(/\/o\/([^?]+)/);
        if (!urlMatch) return null;
        const storagePath = decodeURIComponent(urlMatch[1]);
        const bytes = await getBytes(ref(storage, storagePath));
        let blobType = 'image/png';
        if (storagePath.endsWith('.svg')) blobType = 'image/svg+xml';
        else if (storagePath.endsWith('.jpg') || storagePath.endsWith('.jpeg')) blobType = 'image/jpeg';
        else if (storagePath.endsWith('.gif')) blobType = 'image/gif';
        else if (storagePath.endsWith('.webp')) blobType = 'image/webp';
        const blob = new Blob([bytes], { type: blobType });
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const dataUrl = reader.result;
            if (storagePath.endsWith('.svg') || blob.type === 'image/svg+xml') {
              const img = new Image();
              img.onload = () => {
                try {
                  const canvas = document.createElement('canvas');
                  canvas.width = img.naturalWidth || 200;
                  canvas.height = img.naturalHeight || 50;
                  canvas.getContext('2d').drawImage(img, 0, 0);
                  resolve(canvas.toDataURL('image/png'));
                } catch {
                  resolve(dataUrl);
                }
              };
              img.onerror = () => resolve(dataUrl);
              img.src = dataUrl;
            } else resolve(dataUrl);
          };
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      }
      if (url.startsWith('http') || url.startsWith('/')) {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch');
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      }
      return null;
    } catch {
      return null;
    }
  };

  let logoDataUrl = null;
  let logoDimensions = { width: 200, height: 50 };
  if (companyLogo) {
    const isFs = companyLogo.includes('firebasestorage.googleapis.com') || companyLogo.includes('firebasestorage.app');
    logoDataUrl = await Promise.race([
      loadLogoAsDataUrl(companyLogo).catch(() => null),
      new Promise((r) => setTimeout(() => r(null), isFs ? 5000 : 2000)),
    ]);
  }
  if (!logoDataUrl) {
    logoDataUrl = await Promise.race([
      loadLogoAsDataUrl('/bba-logo.png').catch(() => null),
      new Promise((r) => setTimeout(() => r(null), 2000)),
    ]);
  }
  if (logoDataUrl) {
    const img = new Image();
    await Promise.race([
      new Promise((resolve) => {
        img.onload = () => {
          logoDimensions = { width: img.naturalWidth || 200, height: img.naturalHeight || 50 };
          resolve();
        };
        img.onerror = () => resolve();
        img.src = logoDataUrl;
      }),
      new Promise((r) => setTimeout(r, 2000)),
    ]);
  }

  const escapeHtml = (text) =>
    String(text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const richHtml = sopJsonToHtml(contentJSON);

  const tempContainer = document.createElement('div');
  Object.assign(tempContainer.style, {
    position: 'absolute',
    left: '-9999px',
    top: '0',
    width: '800px',
    backgroundColor: 'white',
    color: 'black',
    padding: '40px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
    lineHeight: '1.6',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  });
  tempContainer.innerHTML = `
    <style>
      .doc-pdf h2 { font-size: 20px; font-weight: bold; margin: 16px 0 8px; color: #111; }
      .doc-pdf h3 { font-size: 18px; font-weight: bold; margin: 0 0 8px; color: #111; }
      .doc-pdf p { margin: 6px 0; font-size: 14px; color: #000; line-height: 1.6; }
      .doc-pdf ul { list-style: disc; padding-left: 22px; margin: 6px 0; }
      .doc-pdf ul ul { list-style: circle; }
      .doc-pdf ul ul ul { list-style: square; }
      .doc-pdf ol { list-style: decimal; padding-left: 22px; margin: 6px 0; }
      .doc-pdf li { font-size: 14px; color: #000; margin: 4px 0; }
    </style>
    <div style="text-align: center; margin-bottom: 24px;">
      <h1 style="font-size: 28px; font-weight: bold; color: black; margin-bottom: 10px; word-wrap: break-word;">${escapeHtml(title)}</h1>
    </div>
    <div class="doc-pdf">${richHtml}</div>
  `;
  document.body.appendChild(tempContainer);

  await new Promise((resolve) => setTimeout(resolve, 300));

  const canvas = await html2canvas(tempContainer, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    width: 800,
    height: tempContainer.scrollHeight,
    logging: false,
  });
  document.body.removeChild(tempContainer);

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const pageMargin = 10;
  const logoHeight = logoDataUrl ? 20 : 0;
  const logoBottomMargin = logoDataUrl ? 5 : 0;
  const headerHeight = logoDataUrl ? logoHeight + logoBottomMargin : 0;
  const footerHeight = 15;
  const contentAreaHeight = pdfHeight - pageMargin - headerHeight - pageMargin - footerHeight;
  const imgWidth = pdfWidth - pageMargin * 2;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  const addPageHeaderFooter = (pageNum, totalPages) => {
    if (logoDataUrl) {
      try {
        const maxLogoHeight = 20;
        const maxLogoWidth = 70;
        const ratio = logoDimensions.width / logoDimensions.height;
        let h = maxLogoHeight;
        let w = h * ratio;
        if (w > maxLogoWidth) {
          w = maxLogoWidth;
          h = w / ratio;
        }
        pdf.addImage(logoDataUrl, 'PNG', (pdfWidth - w) / 2, pageMargin, w, h);
      } catch (err) {
        console.error('Error adding logo to PDF:', err);
      }
    }
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    if (companyName) pdf.text(companyName, pageMargin, pdfHeight - pageMargin);
    const currentDate = new Date().toLocaleDateString();
    pdf.text(currentDate, (pdfWidth - pdf.getTextWidth(currentDate)) / 2, pdfHeight - pageMargin);
    const pageText = `Page ${pageNum} of ${totalPages}`;
    pdf.text(pageText, pdfWidth - pageMargin - pdf.getTextWidth(pageText), pdfHeight - pageMargin);
  };

  const baseContentAreaHeight = contentAreaHeight - 30;
  const estimatedLineHeight = 28;
  const estimatedLineHeightInMM = (estimatedLineHeight / canvas.height) * imgHeight;
  const roundDownToLineBoundary = (position) => {
    if (position <= 0) return 0;
    const px = (position / imgHeight) * canvas.height;
    return ((Math.floor(px / estimatedLineHeight) * estimatedLineHeight) / canvas.height) * imgHeight;
  };

  let currentY = 0;
  const pages = [];
  while (currentY < imgHeight) {
    const remaining = imgHeight - currentY;
    if (remaining <= baseContentAreaHeight) {
      pages.push({ startY: currentY, height: imgHeight - currentY });
      break;
    }
    const roundedDownEnd = roundDownToLineBoundary(currentY + baseContentAreaHeight);
    const safeEndY = Math.min(roundedDownEnd + estimatedLineHeightInMM, imgHeight);
    const finalEndY = Math.min(Math.max(safeEndY, currentY + estimatedLineHeightInMM * 3), imgHeight);
    pages.push({ startY: currentY, height: finalEndY - currentY });
    currentY = finalEndY;
  }

  const totalPages = pages.length;
  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    if (pageNum > 1) pdf.addPage();
    addPageHeaderFooter(pageNum, totalPages);
    const page = pages[pageNum - 1];
    const sourceYInCanvas = (page.startY / imgHeight) * canvas.height;
    const sourceHeightInCanvas = (page.height / imgHeight) * canvas.height;
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = sourceHeightInCanvas;
    pageCanvas
      .getContext('2d')
      .drawImage(canvas, 0, sourceYInCanvas, canvas.width, sourceHeightInCanvas, 0, 0, canvas.width, sourceHeightInCanvas);
    pdf.addImage(pageCanvas.toDataURL('image/png'), 'PNG', pageMargin, pageMargin + headerHeight, imgWidth, page.height);
  }

  const fileName = `${String(title || 'document').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${fileSuffix}.pdf`;
  pdf.save(fileName);
}
